/**
 * OPS-STAB-005 — unified recommendation lifecycle visibility (dual-shell).
 * Derives display state from clinical recommendation rows + optional execution hints.
 * No auto-progression; visibility and queue filters only.
 */

import { normalizeClinicalRecommendationType } from './clinicalRecommendationContract.js';

export const RECOMMENDATION_LIFECYCLE_STAGES = [
  'RECOMMENDED',
  'CONFIRMED',
  'REGISTERED',
  'BILLED',
  'IN_PROGRESS',
  'VERIFIED',
  'PRINTED',
  'COMPLETED',
  'CANCELLED',
  'EXPIRED',
];

export const RECOMMENDATION_STATUS_CHIPS = {
  RECOMMENDED: { label: 'Recommended', tone: 'neutral' },
  CONFIRMED: { label: 'Confirmed', tone: 'info' },
  REGISTERED: { label: 'Registered', tone: 'info' },
  BILLED: { label: 'Billed', tone: 'success' },
  IN_PROGRESS: { label: 'In progress', tone: 'warning' },
  VERIFIED: { label: 'Verified', tone: 'success' },
  PRINTED: { label: 'Printed', tone: 'success' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  CANCELLED: { label: 'Cancelled', tone: 'muted' },
  EXPIRED: { label: 'Expired', tone: 'danger' },
};

/** Client-side operational queue filters (no new backend queues). */
export const RECOMMENDATION_QUEUE_FILTERS = [
  { id: 'ALL', label: 'All' },
  { id: 'PENDING_CONFIRMATION', label: 'Pending confirmations' },
  { id: 'PENDING_REGISTRATION', label: 'Pending registration' },
  { id: 'PENDING_BILLING', label: 'Pending billing' },
  { id: 'PENDING_SAMPLE', label: 'Pending sample collection' },
  { id: 'PENDING_SCAN', label: 'Pending scan' },
  { id: 'PENDING_REPORT', label: 'Pending reports' },
  { id: 'TERMINAL', label: 'Cancelled / expired' },
];

const LAB_SAMPLE_STATES = new Set(['REGISTERED', 'COLLECTION_PENDING']);
const LAB_REPORT_STATES = new Set(['COLLECTED', 'PROCESSING', 'RESULT_ENTERED']);
const RAD_SCAN_STATES = new Set(['REGISTERED', 'SCHEDULED', 'IN_PROGRESS']);
const RAD_REPORT_STATES = new Set(['COMPLETED_PENDING_REPORT', 'REPORT_ENTERED']);

/**
 * @param {object} rec — clinical recommendation row
 * @param {{ orderState?: string, billed?: boolean, verified?: boolean, printed?: boolean }} [executionHint]
 */
export function deriveLifecycleStage(rec, executionHint) {
  const status = rec?.recommendationStatus;
  if (status === 'CANCELLED') return 'CANCELLED';
  if (status === 'EXPIRED') return 'EXPIRED';
  const orderState = executionHint?.orderState;
  if (executionHint?.printed) return 'PRINTED';
  if (executionHint?.verified || orderState === 'VERIFIED') return 'VERIFIED';
  if (orderState === 'COMPLETED' || orderState === 'RELEASED') return 'COMPLETED';
  if (executionHint?.billed) return 'BILLED';
  if (orderState && orderState !== 'REGISTERED') return 'IN_PROGRESS';
  if (rec?.operationalOrderId) return 'REGISTERED';
  if (status === 'CONFIRMED' || status === 'PARTIALLY_CONFIRMED') return 'CONFIRMED';
  return 'RECOMMENDED';
}

/**
 * @returns {{ code: string, label: string, blocking?: boolean }}
 */
export function derivePendingAction(rec, executionHint) {
  const stage = deriveLifecycleStage(rec, executionHint);
  const module = normalizeClinicalRecommendationType(
    rec?.recommendationType ?? rec?.operationalDomain ?? 'CLINICAL',
  );
  const label = rec?.itemDisplaySnapshot || rec?.itemCodeSnapshot || rec?.mappingId || 'Investigation';
  const orderState = executionHint?.orderState;

  if (stage === 'CANCELLED') {
    return { code: 'CANCELLED', label: 'Cancelled recommendation', blocking: false };
  }
  if (stage === 'EXPIRED') {
    return { code: 'EXPIRED', label: 'Expired recommendation', blocking: true };
  }
  if (stage === 'RECOMMENDED') {
    return { code: 'PENDING_CONFIRMATION', label: `Pending confirmation — ${label}`, blocking: true };
  }
  if (stage === 'CONFIRMED') {
    return { code: 'PENDING_REGISTRATION', label: `Pending registration — ${label}`, blocking: true };
  }
  if (stage === 'REGISTERED' && !executionHint?.billed) {
    return { code: 'PENDING_BILLING', label: `Pending billing — ${label}`, blocking: true };
  }
  if (module === 'LAB' && orderState && LAB_SAMPLE_STATES.has(orderState)) {
    return { code: 'PENDING_SAMPLE', label: `Pending sample collection — ${label}`, blocking: true };
  }
  if (module === 'LAB' && orderState && LAB_REPORT_STATES.has(orderState)) {
    return { code: 'PENDING_REPORT', label: `Pending lab report — ${label}`, blocking: true };
  }
  if (module === 'RADIOLOGY' && orderState && RAD_SCAN_STATES.has(orderState)) {
    return { code: 'PENDING_SCAN', label: `Pending scan — ${label}`, blocking: true };
  }
  if (module === 'RADIOLOGY' && orderState && RAD_REPORT_STATES.has(orderState)) {
    return { code: 'PENDING_REPORT', label: `Pending radiology report — ${label}`, blocking: true };
  }
  if (stage === 'BILLED' || stage === 'IN_PROGRESS') {
    return { code: 'IN_FLIGHT', label: `In progress — ${label}`, blocking: false };
  }
  return { code: 'NONE', label: 'No pending action', blocking: false };
}

export function recommendationDisplayName(rec) {
  return rec?.itemDisplaySnapshot || rec?.itemCodeSnapshot || rec?.mappingId || `#${rec?.recommendationId}`;
}

export function recommendationModuleLabel(rec) {
  const t = normalizeClinicalRecommendationType(rec?.recommendationType ?? rec?.operationalDomain);
  if (t === 'LAB') return 'Lab';
  if (t === 'RADIOLOGY') return 'Radiology';
  if (t === 'PHARMACY') return 'Pharmacy';
  return t ?? 'Clinical';
}

/**
 * @param {object} rec
 * @param {string} filterId — RECOMMENDATION_QUEUE_FILTERS id
 * @param {object} [executionHint]
 */
export function matchesRecommendationQueueFilter(rec, filterId, executionHint) {
  if (!filterId || filterId === 'ALL') return true;
  const action = derivePendingAction(rec, executionHint);
  if (filterId === 'TERMINAL') {
    const stage = deriveLifecycleStage(rec, executionHint);
    return stage === 'CANCELLED' || stage === 'EXPIRED';
  }
  return action.code === filterId;
}

/** Counter-actionable rows only (confirm / register / cancel at counter). */
export function isCounterActionable(rec) {
  if (!rec) return false;
  if (rec.operationalOrderId) return false;
  const s = rec.recommendationStatus;
  return s === 'RECOMMENDED' || s === 'CONFIRMED';
}

/** Front office: pending + expired without lab/rad execution detail. */
export function filterFrontOfficeRecommendations(rows) {
  return (rows ?? []).filter((r) => {
    const stage = deriveLifecycleStage(r);
    return (
      stage === 'RECOMMENDED' ||
      stage === 'CONFIRMED' ||
      stage === 'EXPIRED' ||
      (stage === 'REGISTERED' && !r.operationalOrderId)
    );
  });
}

/**
 * Discharge read-only blockers from unresolved recommendations.
 * @param {object[]} rows — merged LAB + RAD operational visibility rows
 */
export function dischargeBlockersFromRecommendations(rows) {
  return (rows ?? [])
    .map((rec) => {
      const action = derivePendingAction(rec);
      if (!action.blocking && action.code !== 'PENDING_REPORT') return null;
      const stage = deriveLifecycleStage(rec);
      if (stage === 'COMPLETED' || stage === 'VERIFIED' || stage === 'PRINTED') return null;
      if (action.code === 'NONE' || action.code === 'IN_FLIGHT') return null;
      return {
        sourceModule: recommendationModuleLabel(rec).toUpperCase(),
        blockerCode: `REC_${action.code}`,
        message: action.label,
        blocking: action.blocking !== false,
      };
    })
    .filter(Boolean);
}

/**
 * Timeline labels for patient journey (compact).
 */
export function buildRecommendationTimelineEntries(rows) {
  const out = [];
  for (const rec of rows ?? []) {
    const name = recommendationDisplayName(rec);
    const module = recommendationModuleLabel(rec);
    const created = rec.createdAt ? new Date(rec.createdAt) : null;
    out.push({
      occurredAt: created,
      sourceModule: module.toUpperCase(),
      eventType: 'RECOMMENDED',
      label: `Doctor recommended ${name}`,
      reference: String(rec.recommendationId),
    });
    const stage = deriveLifecycleStage(rec);
    if (stage === 'CONFIRMED' || stage === 'REGISTERED' || stage === 'BILLED' || rec.operationalOrderId) {
      out.push({
        occurredAt: created,
        sourceModule: module.toUpperCase(),
        eventType: 'CONFIRMED',
        label: `Counter confirmed ${name}`,
        reference: String(rec.recommendationId),
      });
    }
    if (rec.operationalOrderId || stage === 'REGISTERED' || stage === 'BILLED') {
      out.push({
        occurredAt: created,
        sourceModule: module.toUpperCase(),
        eventType: 'REGISTERED',
        label: `${name} registered`,
        reference: rec.operationalOrderId ? String(rec.operationalOrderId) : String(rec.recommendationId),
      });
    }
    if (stage === 'CANCELLED') {
      out.push({
        occurredAt: created,
        sourceModule: module.toUpperCase(),
        eventType: 'CANCELLED',
        label: `${name} cancelled`,
        reference: String(rec.recommendationId),
      });
    }
    if (stage === 'EXPIRED') {
      out.push({
        occurredAt: rec.expiresAt ? new Date(rec.expiresAt) : created,
        sourceModule: module.toUpperCase(),
        eventType: 'EXPIRED',
        label: `${name} expired`,
        reference: String(rec.recommendationId),
      });
    }
  }
  return out.sort((a, b) => {
    const ta = a.occurredAt ? a.occurredAt.getTime() : 0;
    const tb = b.occurredAt ? b.occurredAt.getTime() : 0;
    return tb - ta;
  });
}

export function countPendingByQueue(rows, executionHintsById) {
  const counts = {};
  for (const f of RECOMMENDATION_QUEUE_FILTERS) {
    if (f.id === 'ALL') continue;
    counts[f.id] = 0;
  }
  for (const rec of rows ?? []) {
    const hint = executionHintsById?.[rec.recommendationId];
    for (const f of RECOMMENDATION_QUEUE_FILTERS) {
      if (f.id === 'ALL') continue;
      if (matchesRecommendationQueueFilter(rec, f.id, hint)) counts[f.id]++;
    }
  }
  return counts;
}
